
import { useState } from 'react';

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  interest: string;
  startTime: string;
  message: string;
}

interface ValidationErrors {
  email?: string;
  phone?: string;
}

export default function ContactForm() {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    interest: '',
    startTime: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  // Função para formatar telefone
  const formatPhone = (value: string): string => {
    // Remove todos os caracteres não numéricos
    const numbers = value.replace(/\D/g, '');
    
    // Aplica a formatação (XX) XXXXX-XXXX
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else if (numbers.length <= 11) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    } else {
      // Limita a 11 dígitos
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  // Função para validar email
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Função para obter apenas os números do telefone
  const getPhoneNumbers = (formattedPhone: string): string => {
    return formattedPhone.replace(/\D/g, '');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Formatação específica para telefone
    if (name === 'phone') {
      processedValue = formatPhone(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Validação em tempo real
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      
      if (name === 'email' && value) {
        if (!validateEmail(value)) {
          newErrors.email = 'Por favor, digite um email válido';
        } else {
          delete newErrors.email;
        }
      }
      
      if (name === 'phone' && value) {
        const phoneNumbers = getPhoneNumbers(processedValue);
        if (phoneNumbers.length < 10 || phoneNumbers.length > 11) {
          newErrors.phone = 'Telefone deve ter 10 ou 11 dígitos';
        } else {
          delete newErrors.phone;
        }
      }
      
      return newErrors;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const conversionDateTime = new Intl.DateTimeFormat('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }).format(new Date());

      // Preparar dados no formato do Lovoo CRM
      const dataToSend = {
        api_key: 'e47a0f76-f476-42ef-9b51-a6b17a5f103f',
        nome: formData.name,
        email: formData.email,
        telefone: `+55${getPhoneNumbers(formData.phone)}`,
        origem: 'site-vox-joaopessoa',
        observacao: formData.message,
        data_hora_conversao: conversionDateTime,
        // Campos personalizados conforme documentação
        "9": formData.interest, // Campo interesse com ID 9
        "10": formData.startTime // Campo startTime com ID 10
      };

      // Log detalhado para debug
      console.log('=== DEBUG M4TRACK ===');
      console.log('1. Dados do formulário:', formData);
      console.log('2. Payload que será enviado:', dataToSend);
      console.log('3. URL do webhook:', 'https://webhooks.manager01.m4track.com.br/webhook/site-vox-joaopessoa');
      console.log('4. Ambiente:', window.location.hostname);

      // Verificar se está em ambiente local
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (isLocalhost) {
        // Em ambiente local, simular sucesso para teste
        console.log('🧪 AMBIENTE LOCAL - Simulando envio para M4Track');
        console.log('📤 Dados que seriam enviados:', JSON.stringify(dataToSend, null, 2));
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simular delay da requisição
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          interest: '',
          startTime: '',
          message: ''
        });
        return;
      }

      console.log('🚀 Enviando para M4Track...');

      // Enviar para M4Track usando formato JSON
      const response = await fetch('https://webhooks.manager01.m4track.com.br/webhook/site-vox-joaopessoa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(dataToSend)
      });

      console.log('📡 Resposta recebida:');
      console.log('- Status:', response.status);
      console.log('- Status Text:', response.statusText);
      console.log('- Headers:', Object.fromEntries(response.headers.entries()));

      // Tentar ler a resposta como texto primeiro
      let responseText = '';
      try {
        responseText = await response.text();
        console.log('- Corpo da resposta:', responseText);
      } catch (textError) {
        console.log('- Erro ao ler resposta como texto:', textError);
      }

      if (response.ok || response.status === 200 || response.status === 201) {
        console.log('✅ Sucesso! Lead enviado para M4Track');
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          interest: '',
          startTime: '',
          message: ''
        });
      } else {
        console.error('❌ Erro ao enviar para M4Track');
        console.error('Status:', response.status, response.statusText);
        console.error('Resposta:', responseText);
        
        // Tentar parsear como JSON se possível
        try {
          const jsonResponse = JSON.parse(responseText);
          console.error('Resposta JSON:', jsonResponse);
        } catch (parseError) {
          console.error('Resposta não é JSON válido');
        }
        
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('💥 Erro de rede ou exceção:', error);
      if (error instanceof Error) {
        console.error('Tipo do erro:', error.constructor.name);
        console.error('Mensagem:', error.message);
        if (error.stack) {
          console.error('Stack trace:', error.stack);
        }
      } else {
        console.error('Erro desconhecido:', String(error));
      }
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      console.log('=== FIM DEBUG ===');
    }
  };

  return (
    <section id="contato" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Fale Conosco
          </h2>
          <p className="text-xl text-gray-600">
            Preencha o formulário e nossa equipe entrará em contato
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F19E1A] focus:border-transparent text-sm"
                  placeholder="Seu nome completo"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Seu Melhor E-mail *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#F19E1A] focus:border-transparent text-sm ${
                    validationErrors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="seu@email.com"
                />
                {validationErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Telefone/WhatsApp com DDD *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <span className="text-lg mr-1">🇧🇷</span>
                    <span className="text-gray-500 text-sm">+55</span>
                  </div>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className={`w-full pl-20 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#F19E1A] focus:border-transparent text-sm ${
                      validationErrors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="(11) 99999-9999"
                    maxLength={15}
                  />
                </div>
                {validationErrors.phone && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.phone}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="interest" className="block text-sm font-semibold text-gray-700 mb-2">
                  Interesse Principal
                </label>
                <select
                  id="interest"
                  name="interest"
                  value={formData.interest}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F19E1A] focus:border-transparent text-sm pr-8"
                >
                  <option value="">Selecione uma opção</option>
                  <option value="performance-corporativa">Aumentar Performance Corporativa</option>
                  <option value="eliminar-medo-tensao">Eliminar Medo/Tensão ao falar em público</option>
                  <option value="palestras-apresentacoes">Ministrar Palestras/Apresentações de impacto</option>
                  <option value="videos-atrativos">Engajar com vídeos mais atrativos</option>
                  <option value="outro-motivo">Outro motivo</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="startTime" className="block text-sm font-semibold text-gray-700 mb-2">
                Em quanto tempo você pretende começar seu desenvolvimento em oratória?
              </label>
              <select
                id="startTime"
                name="startTime"
                value={formData.startTime}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F19E1A] focus:border-transparent text-sm pr-8"
              >
                <option value="">Selecione uma opção</option>
                <option value="urgente">Urgente (o quanto antes)</option>
                <option value="proximo-mes">Para o próximo mês</option>
                <option value="ate-3-meses">Em até 3 meses</option>
                <option value="so-pesquisando">Só pesquisando, meu desenvolvimento não é prioridade</option>
              </select>
            </div>


            <div className="mb-6">
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                Mensagem
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F19E1A] focus:border-transparent text-sm resize-none"
                placeholder="Conte-nos mais sobre seus objetivos e expectativas..."
              ></textarea>
              <div className="text-right text-xs text-gray-500 mt-1">
                {formData.message.length}/500 caracteres
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#F19E1A] hover:bg-[#E08A00] text-white font-semibold py-4 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Enviando...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <i className="ri-send-plane-line mr-2"></i>
                  Enviar Mensagem
                </span>
              )}
            </button>

            {submitStatus === 'success' && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  <span className="text-green-800">Mensagem enviada com sucesso! Entraremos em contato em breve.</span>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-600 mr-2"></i>
                  <span className="text-red-800">Erro ao enviar mensagem. Tente novamente.</span>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
